import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../lib/LanguageContext';
import { translations } from '../../lib/translations';
export function MethodSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true,
    margin: '-80px'
  });
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, {
    once: true,
    margin: '-60px'
  });
  const analogyRef = useRef<HTMLDivElement>(null);
  const analogyInView = useInView(analogyRef, {
    once: true,
    margin: '-60px'
  });
  const {
    lang
  } = useLanguage();
  const t = translations[lang].method;
  return <section style={{
    width: '100%',
    backgroundColor: 'var(--black)',
    color: 'var(--white)',
    padding: '10rem 1.5rem'
  }} data-id="element-264">
      <div style={{
      maxWidth: '48rem',
      margin: '0 auto'
    }} data-id="element-265">
        {/* Section label */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} style={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.65rem',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--grey-500)',
        marginBottom: '3rem'
      }} data-id="element-266">
          {t.label}
        </motion.p>

        {/* Title */}
        <motion.div ref={titleRef} initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} style={{
        marginBottom: '6rem'
      }} data-id="element-267">
          <h2 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: 'clamp(1.75rem, 4vw, 3rem)',
          color: 'var(--white)',
          lineHeight: 1.15,
          letterSpacing: '-0.01em',
          margin: 0
        }} data-id="element-268">
            {t.title}
          </h2>
        </motion.div>

        {/* Flow diagram */}
        <div ref={stepsRef} data-id="element-269">
          {t.steps.map((step, i) => <div key={i} data-id="element-270">
              {/* Step node */}
              <motion.div initial={{
            opacity: 0,
            x: -16
          }} animate={stepsInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: i * 0.13
          }} style={{
            display: 'grid',
            gridTemplateColumns: '1.75rem 1fr',
            gap: '1.5rem',
            alignItems: 'flex-start'
          }} data-id="element-271">
                {/* Node dot */}
                <div style={{
              paddingTop: '0.45rem',
              display: 'flex',
              justifyContent: 'center'
            }} data-id="element-272">
                  <div style={{
                width: step.terminal || step.highlight ? '8px' : '6px',
                height: step.terminal || step.highlight ? '8px' : '6px',
                backgroundColor: step.terminal || step.highlight ? 'var(--red)' : 'rgba(255,255,255,0.25)',
                flexShrink: 0
              }} data-id="element-273" />
                </div>

                {/* Content */}
                <div style={{
              paddingBottom: '0.5rem'
            }} data-id="element-274">
                  <div style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: step.terminal ? 700 : step.highlight ? 500 : 400,
                fontSize: step.terminal ? 'clamp(1.1rem, 2vw, 1.25rem)' : 'clamp(0.9375rem, 1.8vw, 1.0625rem)',
                color: step.terminal ? 'var(--white)' : step.highlight ? 'var(--red)' : 'rgba(255,255,255,0.75)',
                lineHeight: 1.4,
                marginBottom: '0.5rem'
              }} data-id="element-275">
                    {step.label}
                  </div>
                  <div style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 300,
                fontSize: '0.875rem',
                color: 'rgba(255,255,255,0.35)',
                lineHeight: 1.75,
                maxWidth: '36rem',
                whiteSpace: 'pre-line'
              }} data-id="element-276">
                    {step.description}
                  </div>
                </div>
              </motion.div>

              {/* Arrow connector */}
              {i < t.steps.length - 1 && <motion.div initial={{
            opacity: 0
          }} animate={stepsInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.4,
            delay: 0.1 + i * 0.13
          }} style={{
            display: 'grid',
            gridTemplateColumns: '1.75rem 1fr',
            gap: '1.5rem',
            padding: '0.5rem 0'
          }} data-id="element-277">
                  <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px'
            }} data-id="element-278">
                    <div style={{
                width: '1px',
                height: '1.75rem',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }} data-id="element-279" />
                    <span style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.15)',
                lineHeight: 1
              }} data-id="element-280">
                      ↓
                    </span>
                  </div>
                  <div data-id="element-281" />
                </motion.div>}
            </div>)}
        </div>

        {/* Mathematical analogy */}
        <motion.div ref={analogyRef} initial={{
        opacity: 0,
        y: 16
      }} animate={analogyInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.2
      }} style={{
        marginTop: '4rem',
        paddingTop: '3rem',
        borderTop: '1px solid rgba(255,255,255,0.07)'
      }} data-id="element-282">
          <p style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: '1.125rem',
          color: 'var(--white)',
          lineHeight: 1.6,
          margin: 0
        }} data-id="element-283">
            {t.closing1}
            <br data-id="element-284" />
            <span style={{
            color: 'rgba(255,255,255,0.4)',
            fontWeight: 300
          }} data-id="element-285">
              {t.closing2}
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}